import { z } from "zod";

/**
 * Shared DTO — Admin config deep-link AI theo iOS/Android/Web (US-AI-0310-S04 · @UC9400 · @gate config).
 *
 * Một nguồn sự thật cho api (controller + DTO validate biên) và admin web (màn config). PO 24/07: mỗi
 * deep-link mang 5 field admin sửa được (Text · Context · iOS · Android · Web) + `route` READ-ONLY.
 *
 * BẤT BIẾN:
 * - Deep-link KHÔNG tạo/xoá được qua API — chỉ OVERRIDE 5 field TRÌNH BÀY của entry có sẵn trong
 *   ROUTE_CATALOG (domain). key lạ ⇒ 404.
 * - 🔴 `route` KHÔNG nằm trong patch schema ⇒ trạng thái "admin đổi route" KHÔNG BIỂU DIỄN ĐƯỢC ở biên;
 *   gửi field cấm ⇒ 400, 0 row chạm DB. Route là tính đúng-đắn có guard đối chiếu app_router.dart.
 * - Kế thừa: default catalog (code) ⊕ override 'system' (DB). Field NULL trong override = kế thừa lại.
 * - `.strict()` — chặn key lạ tại biên (fail-closed input).
 * - PATCH chạm ĐƯỜNG VÀO MÀN GHI TIỀN ⇒ RBAC SUPER_ADMIN (server @Roles) + MỌI PATCH ghi 1 AuditLog.
 */

/** Giới hạn độ dài — chặn abuse, KHÔNG phải quy tắc nghiệp vụ. */
export const AI_DEEP_LINK_TEXT_MAX_LEN = 200;
export const AI_DEEP_LINK_CONTEXT_MAX_LEN = 500;
export const AI_DEEP_LINK_URL_MAX_LEN = 2048;

/**
 * URL ngoài cho iOS/Android — scheme TUỲ Ý (`goppy://…` custom scheme HOẶC `https://…` App Links).
 * `z.string().url()` chấp nhận custom scheme (URL constructor parse `goppy://x` hợp lệ) — có chủ đích.
 */
const externalAppUrlSchema = z.string().url().max(AI_DEEP_LINK_URL_MAX_LEN);

/**
 * URL Web — BẮT BUỘC https (trang web công khai, không cho scheme lạ / http trần).
 * 🔴 fail-closed: `http://` hay scheme khác ⇒ THROW ở biên, 0 row chạm DB.
 */
const externalWebUrlSchema = z
  .string()
  .url()
  .max(AI_DEEP_LINK_URL_MAX_LEN)
  .refine((u) => u.startsWith("https://"), { message: "web phải là https://" });

/** Giá trị hiệu lực của 5 field trình bày (catalog ⊕ override) — đều là string đã giải. */
export const aiDeepLinkFieldsSchema = z
  .object({
    textVi: z.string(),
    textEn: z.string(),
    contextVi: z.string(),
    ios: z.string(),
    android: z.string(),
    web: z.string(),
  })
  .strict();
export type AiDeepLinkFields = z.infer<typeof aiDeepLinkFieldsSchema>;

/**
 * Override đã lưu (system scope). Mỗi field NULL = "không override field đó" (kế thừa catalog).
 * `override === null` ở item nghĩa là CHƯA từng override (không có row).
 */
export const aiDeepLinkOverrideSchema = z
  .object({
    textVi: z.string().nullable(),
    textEn: z.string().nullable(),
    contextVi: z.string().nullable(),
    ios: z.string().nullable(),
    android: z.string().nullable(),
    web: z.string().nullable(),
    updatedBy: z.string(),
    updatedAt: z.string(),
  })
  .strict();
export type AiDeepLinkOverride = z.infer<typeof aiDeepLinkOverrideSchema>;

/**
 * Một entry deep-link trong màn admin: key + route (READ-ONLY) + giá trị hiệu lực + default catalog +
 * override thô (để UI biết field nào đang lệch khỏi catalog).
 */
export const adminAiDeepLinkItemSchema = z
  .object({
    key: z.string(),
    /** Route Flutter — READ-ONLY (admin thấy link dẫn màn nào, KHÔNG sửa). */
    route: z.string(),
    /** Tên tham số route (`fundId`…) — để UI hiện link cần dữ liệu gì. */
    params: z.array(z.string()),
    /** Giá trị HIỆU LỰC (catalog ⊕ override) — thứ AI dùng bây giờ. */
    effective: aiDeepLinkFieldsSchema,
    /** Default BẤT BIẾN từ catalog (code) — admin thấy mình đang lệch khỏi cái gì / để "về mặc định". */
    defaults: aiDeepLinkFieldsSchema,
    /** Override đã lưu (null = chưa override). Field null trong đây = kế thừa catalog. */
    override: aiDeepLinkOverrideSchema.nullable(),
  })
  .strict();
export type AdminAiDeepLinkItem = z.infer<typeof adminAiDeepLinkItemSchema>;

/** Response GET /v1/admin/ai/deep-links. */
export const adminAiDeepLinksResponseSchema = z
  .object({
    items: z.array(adminAiDeepLinkItemSchema),
  })
  .strict();
export type AdminAiDeepLinksResponse = z.infer<typeof adminAiDeepLinksResponseSchema>;

/**
 * Body PATCH /v1/admin/ai/deep-links/:key — override 5 field trình bày.
 *
 * Mỗi field NULLABLE, BẮT BUỘC có mặt (form gửi đủ trạng thái): value = đặt override; `null` = kế thừa
 * catalog field đó. Cả 6 field null ⇒ service XOÁ row override (kế thừa toàn bộ).
 *
 * 🔴 `route` KHÔNG có ở đây — `.strict()` ⇒ gửi `route` (hay bất kỳ key lạ) ⇒ 400, 0 row chạm DB.
 * Đây là chốt chặn "admin không trỏ app tới màn không tồn tại" ở TẦNG CONTRACT.
 */
export const patchAdminAiDeepLinkSchema = z
  .object({
    textVi: z.string().max(AI_DEEP_LINK_TEXT_MAX_LEN).nullable(),
    textEn: z.string().max(AI_DEEP_LINK_TEXT_MAX_LEN).nullable(),
    contextVi: z.string().max(AI_DEEP_LINK_CONTEXT_MAX_LEN).nullable(),
    ios: externalAppUrlSchema.nullable(),
    android: externalAppUrlSchema.nullable(),
    web: externalWebUrlSchema.nullable(),
  })
  .strict();
export type PatchAdminAiDeepLinkRequest = z.infer<typeof patchAdminAiDeepLinkSchema>;
